import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { ResolutionString } from '../../../charting_library';
import { DEFAULT_STARRED_RESOLUTIONS } from '../../../constants/tradingview';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { SelectWithFavorites } from '../../SelectWithFavorites/SelectWithFavorites';

interface OptionWithShortLabel {
  label: string;
  value: ResolutionString;
  shortLabel: string;
}

// Resolutions with full labels (shown in dropdown) and short labels (shown inline if favorite)
const RESOLUTION_GROUPS: Array<{ title?: string; options: OptionWithShortLabel[] }> = [
  {
    title: 'Minutes',
    options: [
      { label: '1 minute', value: '1' as ResolutionString, shortLabel: '1m' },
      { label: '3 minutes', value: '3' as ResolutionString, shortLabel: '3m' },
      { label: '5 minutes', value: '5' as ResolutionString, shortLabel: '5m' },
      { label: '15 minutes', value: '15' as ResolutionString, shortLabel: '15m' },
      { label: '30 minutes', value: '30' as ResolutionString, shortLabel: '30m' },
    ],
  },
  {
    title: 'Hours',
    options: [
      { label: '1 hour', value: '60' as ResolutionString, shortLabel: '1h' },
      { label: '2 hours', value: '120' as ResolutionString, shortLabel: '2h' },
      { label: '4 hours', value: '240' as ResolutionString, shortLabel: '4h' },
      { label: '8 hours', value: '480' as ResolutionString, shortLabel: '8h' },
      { label: '12 hours', value: '720' as ResolutionString, shortLabel: '12h' },
    ],
  },
  {
    title: 'Days',
    options: [
      { label: '1 day', value: '1D' as ResolutionString, shortLabel: 'D' },
      { label: '3 days', value: '3D' as ResolutionString, shortLabel: '3d' },
      { label: '1 week', value: '1W' as ResolutionString, shortLabel: 'W' },
      { label: '1 month', value: '1M' as ResolutionString, shortLabel: 'M' },
    ],
  },
];

// Flatten resolution groups into array
const flattenedResolutions = RESOLUTION_GROUPS.flatMap((group) =>
  group.options.map((option) => option.value)
);

export const ChartResolutionSelector = ({
  value,
  onChange,
}: {
  value: ResolutionString;
  onChange: (resolution: ResolutionString) => void;
}) => {
  const [starredChartResolutions, setStarredChartResolutions] = useLocalStorage<ResolutionString[]>(
    localStorageKeys.starredChartResolutions,
    DEFAULT_STARRED_RESOLUTIONS
  );

  // Get all resolutions in order from the groups

  const handleToggleStarResolution = useCallback(
    (resolution: ResolutionString) => {
      setStarredChartResolutions((prev) =>
        prev.includes(resolution)
          ? // Remove star
            prev.filter((r) => r !== resolution)
          : // Add star, sorting based RESOLUTION_GROUPS order
            [...prev, resolution].sort(
              (a, b) => flattenedResolutions.indexOf(a) - flattenedResolutions.indexOf(b)
            )
      );
    },
    [setStarredChartResolutions]
  );

  return (
    <SelectWithFavorites<ResolutionString, OptionWithShortLabel>
      options={RESOLUTION_GROUPS}
      value={value}
      onChange={(value) => (value !== undefined ? onChange(value) : null)}
      starredValues={starredChartResolutions}
      onToggleStar={handleToggleStarResolution}
      shortcutComponent={({ option: { value, shortLabel } }) => (
        <div className="flex h-4 min-w-4 items-center justify-center leading-none">
          {shortLabel || value}
        </div>
      )}
      shortcutButtonClassName="text-xs font-normal tabular-nums"
      triggerButtonClassName="text-xs font-normal tabular-nums"
    />
  );
};
